import {QuotePriceLine, QuotePriceRider} from "../../../../api/QuotePricing";
import {FetchContext} from "../../../../misc/scroller/Table";
import {api} from "../../../../api/API";
import {getAll} from "../../../logistics/routes/useGetAll";
import {priceDetailEmitter} from "../UnPricedItems";
import {getRowCategoryBase, PriceRow} from "./Common";

function makeList(prices: QuotePriceLine[], riders: QuotePriceRider[]) {
    let out: PriceRow[] = [];
    let defaultSortCounter = 0

    prices.forEach(p => {
        out.push({
            defaultSort: defaultSortCounter,
            id: p.id,
            kind: "quote-price",
            value: p,
        })
    })

    riders.forEach(r => {
        out.push({
            defaultSort: defaultSortCounter,
            id: r.id,
            kind: "quote-price-rider",
            value: r,
        });
    });

    riderSections.map((k, index) => {
        // hardware rider-create rows are injected per supplier-category subtab by
        // QuoteHardware (see hardwareRiderSectionKey), so skip the generic one here.
        if(k.productType === "hardware") return;

        out.push({
            defaultSort: defaultSortCounter,
            // stable id per section: deriving it from out.length made the id shift
            // whenever the price/rider count changed (e.g. removing a rider), which
            // dropped the row out of originalSortOrder and floated "Add Rider" to the
            // top of its section on refresh.
            id: -(index + 1),
            kind: "quote-price-rider-create",
            sectionKey: k.key as any,
            productType: k.productType,
            name: "",
        });
    })

    return out
}

// hardwareRiderSectionKey is the per-category storage key for quote hardware
// riders, e.g. "hardware-riders/cat-5". getSectionKeyForRiders collapses these
// back to the single "hardware-riders" display group.
export function hardwareRiderSectionKey(rootCategoryId: number) {
    return "hardware-riders/cat-" + rootCategoryId;
}

// hardwareRiderCategoryId returns the root category id a hardware rider section key
// belongs to, or null for the legacy "hardware-riders" key (the hardware-provider
// tab). Returns undefined for non-hardware-rider keys.
export function hardwareRiderCategoryId(sectionKey: string): number | null | undefined {
    if(sectionKey === "hardware-riders") return null;
    const prefix = "hardware-riders/cat-";
    if(!sectionKey.startsWith(prefix)) return undefined;
    const id = parseInt(sectionKey.slice(prefix.length), 10);
    return isNaN(id) ? undefined : id;
}

export const riderSections = [
    {productType: "frame", sectionName: "Frame", key: "frame-riders"},
    {productType: "door", sectionName: "Hollow Metal Door", key: "door-riders/hm"},
    {productType: "door", sectionName: "Wood Door", key: "door-riders/wd"},
    {productType: "hardware", sectionName: "Hardware", key: "hardware-riders"},
    {productType: "div-10", sectionName: "Div 10: W/R Acc", key: "div-10/wr-accessory-riders"},
    {productType: "div-10", sectionName: "Div 10: W/R Partition", key: "div-10/wr-partition-riders"},
    {productType: "div-10", sectionName: "Div 10: Locker", key: "div-10/locker-riders"},
    {productType: "div-10", sectionName: "Div 10: Mailbox", key: "div-10/mailbox-riders"},
    {productType: "div-10", sectionName: "Div 10: Corner Guard", key: "div-10/corner-guard-riders"},
    {productType: "glazing", sectionName: "Glazing", key: "glazing-riders"},
];

export async function getQuotePricing(ctx: FetchContext, props: {
    project: number,
    alternative: number,
    customSort?: (rs: QuotePriceLine[]) => void
}) {
    const {project, alternative} = props;
    const pricesP = getAll(ctx, offset =>
        api.quotePricing.list({project: project, alternative, offset})
    )

    const ridersP = getAll(ctx, offset =>
        api.quotePricing.listRiders({project: project, alternative, offset})
    )

    const rs = await pricesP;
    const riders = await ridersP;

    if(props.customSort) {
        props.customSort(rs);
    } else {
        rs.sort(defaultPriceSortFunc)
    }

    const misc = await getAll(ctx, offset => api.quoteMiscPricing.list({project: project, alternative, offset}))

    priceDetailEmitter.emit([...rs, ...misc.map(m =>({
            ...m,
            productType: "misc",
        })) as any[]
    ]);
    const list = makeList(rs, riders);
    return list
}

export function defaultPriceSortFunc(a: QuotePriceLine, b: QuotePriceLine) {
    const aVal = getRowCategoryBase(a, true);
    const bVal = getRowCategoryBase(b, true);

    if(aVal < bVal) return -1;
    if(aVal > bVal) return 1;
    return 0
}

export async function quotePriceChange(input: PriceRow, opts: {
    project: number;
    alternative: number;
}) {
    if(input.kind === "quote-price") {
        const result = await api.quotePricing.upsert(input.value)
        return Object.assign({}, input, {
            value: result,
        })
    }

    if(input.kind === "quote-price-rider") {
        const result = await api.quotePricing.upsertRider(input.value)
        return Object.assign({}, input, {
            value: result,
        })
    }

    if(input.kind === "quote-price-rider-create") {
        const result = await api.quotePricing.upsertRider({
            id: 0,
            project: opts.project,
            quoteAlternative: opts.alternative,
            productType: input.productType,
            sectionKey: input.sectionKey,
            name: input.name,
            unitCostCents: 0,
            qty: 0,
            markupCents: 0,
            markupPercent: undefined,
            extendedPrice: 0,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            updatedBy: 0,
        })

        return {
            id: input.id,
            kind: "quote-price-rider",
            value: result,
        }
    }

    return input
}